import { Category, Module } from "@apis/index";
import AccessDenied from "@components/access-denied";
import { deletesvg, edit, switchOff, switchOn } from "@assets/icons";
import Header from "@commons/header";
import Column from "@components/column";
import DeleteModal from "@components/delete-modal";
import Svg from "@components/svg";
import Table from "@components/table";
import { useModal } from "@context/ModalContext";
import useConfig from "@hooks/useConfig";
import { RootState } from "@store/index";
import { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IconImage } from "./styles";
import { FaStore } from "react-icons/fa";
import StoreModal from "../../category/list/StoreModal";
import { useQueryState } from "@hooks/useQueryState";

const SubCategoryList = () => {
  const { bucketUrl } = useConfig();
  const {
    items: subcategories = [],
    totalItems,
    totalPages,
  } = useSelector((state: RootState) => state.subcategories);
  const { showModal } = useModal();
  const [page, setPage] = useQueryState<number>("page", 1);
  const [search, setSearch] = useState("");
  const [selectedParentId, setSelectedParentId] = useState<string>("");
  const [parents, setParents] = useState<any[]>([]);

  const [error, setError] = useState<string | null>(null);

  const fetchSubCategories = useCallback(async () => {
    setError(null);
    try {
      await Category.list({
        state: "subcategories",
        params: {
          page,
          limit: 30,
          search,
          type: "subcategory",
          ...(selectedParentId && { parentId: Number(selectedParentId) }),
        },
      });
    } catch (err: any) {
      console.error("Failed to fetch sub-categories:", err);
      setError(err.message || "Failed to load sub-categories");
    }
  }, [page, search, selectedParentId]);

  const fetchParents = useCallback(async () => {
    try {
      const res: any = await Category.list({
        params: { parentId: 0, limit: 1000 },
      });
      if (res?.items) {
        setParents(res.items);
      }
    } catch (err) {
      console.error("Failed to fetch parent categories:", err);
    }
  }, []);

  useEffect(() => {
    fetchParents();
  }, [fetchParents]);

  useEffect(() => {
    fetchSubCategories();
  }, [fetchSubCategories]);

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    setPage(1);
  }, [search, selectedParentId]);

  const columns = [
    {
      title: "Icon",
      key: "icon",
      render: (item: any) => (
        <IconImage src={`${bucketUrl}/${item.icon}`} alt={item.name} />
      ),
    },
    {
      title: "Name",
      key: "name",
      render: (item: any) => item?.name,
    },
    {
      title: "Category",
      key: "category",
      render: (item: any) => item?.parent?.name,
    },
    {
      title: "Store Type",
      key: "type",
      render: (item: any) => item?.type?.name || "N/A",
    },
    {
      title: "Active",
      key: "isActive",
      render: (item: any) => (
        <Svg src={item?.isActive ? switchOn : switchOff} />
      ),
    },
  ];
  const navigate = useNavigate();

  if (error) {
    return (
      <Column>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Header title="All Sub-Category" />
        </div>
        <AccessDenied
          message={error}
          onRetry={fetchSubCategories}
        />
      </Column>
    );
  }

  return (
    <Column>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Header title="All Sub-Category" />
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <select
            value={selectedParentId}
            onChange={(e) => setSelectedParentId(e.target.value)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "1px solid #e2e8f0",
              backgroundColor: "white",
              fontSize: "14px",
              color: "#475569",
              outline: "none",
              cursor: "pointer",
              minWidth: "200px",
              height: "40px",
              marginTop: "20px",
            }}
          >
            <option value="">All Categories</option>
            {parents.map((parent) => (
              <option key={parent.id} value={parent.id}>
                {parent.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <Table
        hasSearchbar={true}
        onSearchChange={setSearch}
        data={subcategories}
        columns={columns}
        totalItems={totalItems}
        pageControlled={page}
        totalPages={totalPages}
        onPageChange={setPage}
        itemsPerPage={30}
        actions={[
          {
            key: "edit",
            icon: edit,
            onClick: (item) => {
              navigate(
                `/admin/product-management/categories/${item.id}?tab=Sub-Category`,
                {
                  state: item,
                },
              );
            },
          },
          {
            key: "remove",
            icon: deletesvg,
            onClick: async (item) => {
              showModal(
                <DeleteModal
                  state="subcategories"
                  api={Category}
                  id={item.id}
                  onSuccess={() => fetchSubCategories()}
                />,
              );
            },
          },
          {
            key: "stores",
            icon: <FaStore size={20} color="#4A4A4A" />,
            onClick: (item) => {
              showModal(
                <StoreModal categoryId={item.id} moduleId={item?.module?.id} />,
                { width: 900 },
              );
            },
          },
        ]}
      />
    </Column>
  );
};

export default SubCategoryList;
